import { useMemo, useState } from 'react';
import { updateMyProfile } from '../../api/auth/authApi';
import { setAuthTokens } from '../../auth/tokenStorage';
import useAutoClearMessage from '../../hooks/useAutoClearMessage';

const PENDING_SOCIAL_SIGNUP_KEY = 'pendingSocialSignup';
const PENDING_KAKAO_SIGNUP_KEY = 'pendingKakaoSignup';
const TERMS_URL = process.env.REACT_APP_TERMS_URL || '/terms';
const PRIVACY_URL = process.env.REACT_APP_PRIVACY_URL || '/privacy';
const MARKETING_CONSENT_URL =
  process.env.REACT_APP_MARKETING_CONSENT_URL || '/marketing-consent';

function parsePendingSignup() {
  const pendingKeys = [PENDING_SOCIAL_SIGNUP_KEY, PENDING_KAKAO_SIGNUP_KEY];
  for (const pendingKey of pendingKeys) {
    const rawValue = sessionStorage.getItem(pendingKey);
    if (!rawValue) {
      continue;
    }
    try {
      const parsed = JSON.parse(rawValue);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (error) {
      // continue
    }
  }
  return null;
}

function ClientSignupPage() {
  const pendingSignup = useMemo(() => parsePendingSignup(), []);
  const hasEmail = Boolean(String(pendingSignup?.email || '').trim());
  const [nickname, setNickname] = useState(pendingSignup?.nickname || '');
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [agreedMarketing, setAgreedMarketing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  useAutoClearMessage(errorMessage, setErrorMessage);
  const hasRequiredConsents = agreedTerms && agreedPrivacy;
  const providerLabel = String(pendingSignup?.provider || 'SOCIAL').toUpperCase();

  if (!pendingSignup) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
        <section className="w-full max-w-[520px] rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
          <h1 className="m-0 text-xl font-semibold text-slate-900">
            회원가입 정보를 찾을 수 없습니다
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            소셜 로그인 후 회원가입 단계로 다시 진입해 주세요.
          </p>
          <button
            type="button"
            className="mt-5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
            onClick={() => window.location.replace('/auth/login')}
          >
            로그인 화면으로 이동
          </button>
        </section>
      </main>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedNickname = nickname.trim();

    if (!trimmedNickname) {
      setErrorMessage('닉네임을 입력해 주세요.');
      return;
    }

    if (!agreedTerms || !agreedPrivacy) {
      setErrorMessage('이용약관 및 개인정보 처리방침(필수) 동의가 필요합니다.');
      return;
    }
    if (!hasEmail) {
      setErrorMessage('소셜 로그인 이메일 제공 동의가 필요합니다. 다시 로그인해 주세요.');
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);

    try {
      if (!pendingSignup.accessToken || !pendingSignup.refreshToken) {
        throw new Error('로그인 토큰을 확인할 수 없습니다.');
      }

      setAuthTokens({
        accessToken: pendingSignup.accessToken,
        refreshToken: pendingSignup.refreshToken,
      });

      await updateMyProfile({
        nickname: trimmedNickname,
        profileImageUrl: pendingSignup.profileImageUrl || null,
        agreedTerms,
        agreedPrivacy,
        agreedMarketing,
      });

      sessionStorage.removeItem(PENDING_SOCIAL_SIGNUP_KEY);
      sessionStorage.removeItem(PENDING_KAKAO_SIGNUP_KEY);
      window.location.replace('/team');
    } catch (error) {
      setErrorMessage(error.message || '회원가입 처리 중 오류가 발생했습니다.');
      setIsSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <section className="w-full max-w-[520px] rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
        <h1 className="m-0 text-2xl font-semibold text-slate-900">회원가입 마무리</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          {providerLabel} 계정으로 가입을 이어갑니다. 아래 정보를 확인하고 가입을 완료해 주세요.
        </p>

        <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
          {!hasEmail && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              이메일 제공 동의가 없어 회원가입을 완료할 수 없습니다. 다시 로그인해 주세요.
            </p>
          )}

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="email">
              이메일
            </label>
            <input
              id="email"
              type="text"
              value={pendingSignup.email || ''}
              readOnly
              className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="nickname">
              닉네임
            </label>
            <input
              id="nickname"
              type="text"
              value={nickname}
              onChange={(event) => setNickname(event.target.value)}
              placeholder="사용할 닉네임을 입력해 주세요"
              className="h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 outline-none focus:border-slate-500"
              maxLength={20}
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={agreedTerms}
              onChange={(event) => setAgreedTerms(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300"
            />
            <span>
              [필수]{' '}
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-slate-800 underline"
              >
                이용약관
              </a>
              에 동의합니다.
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={agreedPrivacy}
              onChange={(event) => setAgreedPrivacy(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300"
            />
            <span>
              [필수]{' '}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-slate-800 underline"
              >
                개인정보 처리방침
              </a>
              에 동의합니다.
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={agreedMarketing}
              onChange={(event) => setAgreedMarketing(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300"
            />
            <span>
              [선택]{' '}
              <a
                href={MARKETING_CONSENT_URL}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-slate-800 underline"
              >
                마케팅 정보 수신 정책
              </a>
              에 동의합니다.
            </span>
          </label>

          {errorMessage && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {errorMessage}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !hasEmail || !hasRequiredConsents}
            className="h-11 w-full rounded-lg bg-slate-900 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {isSubmitting ? '처리 중...' : '회원가입 완료'}
          </button>
        </form>
      </section>
    </main>
  );
}

export default ClientSignupPage;
